/**
 * §36.1 — player onboarding.
 *
 * The ordering is the requirement. Consent is asked **before** a photo is
 * requested, so a player who declines is never asked for a picture at all and no
 * image of them is captured, transmitted, or stored (§36.4). The camera is only
 * reachable from the consenting branch.
 *
 * §36.4 also asks that the two paths read as "a genuine choice between two
 * appealing options, not as a primary action and a penalty" — a player choosing
 * the animal should feel they picked the fun one.
 */

import { useCallback, useState } from "react";
import { Button } from "@tq/ui";
import { api, ApiRequestError, type SessionSnapshot } from "../api.js";
import type { PlayerIdentity } from "./PlayerApp.js";
import { AnimalStep, ChoiceCard, Field, PhotoStep } from "./onboarding-steps.js";

export interface OnboardingProps {
  sessionCode: string;
  sessionTitle: string;
  onComplete: (profileToken: string, snapshot: SessionSnapshot, identity: PlayerIdentity) => void;
  /**
   * The address already proven by a code, when there is one.
   *
   * Present means do not ask again: it is shown, not edited. The server takes
   * the address from the session regardless, so an editable field here would be
   * a box whose contents are silently discarded.
   */
  verifiedEmail?: string | null;
  /**
   * The name the facilitator's invitation list carried, when it carried one.
   * A starting point only: the field stays editable, since the list may say
   * "Kelly, Mary" or a nickname the player would rather not play under.
   */
  suggestedName?: string | null;
}

type Step = "details" | "consent" | "photo" | "animal";

/** details -> look -> photo|animal. The last two are one step, not two. */
const TOTAL_STEPS = 3;

export function Onboarding({
  sessionCode,
  sessionTitle,
  onComplete,
  verifiedEmail = null,
  suggestedName = null,
}: OnboardingProps): React.ReactElement {
  const [step, setStep] = useState<Step>("details");
  const [fullName, setFullName] = useState(suggestedName ?? "");
  const [email, setEmail] = useState(verifiedEmail ?? "");
  const [organization, setOrganization] = useState("");
  const [photo, setPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (input: {
      avatarConsent: boolean;
      photo: string | null;
      avatarAnimal: string | null;
    }) => {
      setBusy(true);
      setError(null);
      try {
        const result = await api.createProfile({
          sessionCode,
          fullName: fullName.trim(),
          email: email.trim(),
          organization: organization.trim() || null,
          ...input,
        });
        // §36.1 step 7 — the lobby is entered immediately. Avatar generation
        // runs behind this and never delays it (§36.3, §31.2.10).
        onComplete(result.profileToken, result.snapshot, {
          sessionPlayerId: result.sessionPlayerId,
          displayName: result.displayName,
          photoUrl: result.photoUrl,
          fullName: result.fullName,
          email: result.email,
        });
      } catch (cause) {
        setError(
          cause instanceof ApiRequestError
            ? cause.message
            : "Could not create your profile. Try again.",
        );
        setBusy(false);
      }
    },
    [sessionCode, fullName, email, organization, onComplete],
  );

  // "photo" and "animal" are the two branches of one choice, not two steps, so
  // they share a number — a player who picks the animal must not be told they
  // have skipped ahead, and one who picks the photo must not be told there is
  // more left than there is.
  const stepNumber = step === "details" ? 1 : step === "consent" ? 2 : 3;

  const detailsValid =
    fullName.trim().length > 1 &&
    // A verified address is already known to be well-formed, and is not typed
    // here, so it cannot be the thing blocking the button.
    (verifiedEmail !== null || /.+@.+\..+/.test(email.trim()));

  return (
    <div className="tq-player">
      <div className="tq-player-main tq-phone-card" style={{ gap: 18, paddingTop: 24 }}>
        <header>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
              color: "var(--muted)",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: ".08em",
            }}
          >
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {sessionTitle.toUpperCase()}
            </span>
            {/* How much of this is left. Onboarding is three steps — details,
                then the look, then whichever branch of it the player picked —
                and without a count it is an unbounded sequence of "Continue"
                buttons in a room where a game is about to start, which is
                exactly where people give up. `aria-live` so it is announced on
                the way through rather than only when focus lands on it. */}
            <span style={{ flex: "none", whiteSpace: "nowrap" }} aria-live="polite">
              STEP {stepNumber} OF {TOTAL_STEPS}
            </span>
          </div>
          <h1 style={{ fontSize: 24, margin: "4px 0 0", color: "#fff" }}>
            {step === "details"
              ? "Let's get you set up"
              : step === "consent"
                ? "Pick your look"
                : step === "photo"
                  ? "Add your photo"
                  : "Choose your character"}
          </h1>
        </header>

        {error && (
          <div role="alert" className="tq-alert">
            {error}
          </div>
        )}

        {step === "details" && (
          <>
            <Field label="Full name" required>
              <input
                className="tq-answer-input"
                type="text"
                autoComplete="name"
                maxLength={120}
                /* The one field between a player and the game, on a screen that
                   opens with nothing else focusable above it. On a phone this
                   does not force the keyboard up on its own, so it costs a
                   player nothing and saves a tap. */
                autoFocus
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
              />
            </Field>

            {verifiedEmail ? (
              // `required` even though it cannot be edited: the address is
              // neither optional nor removable, and labelling a proven identity
              // "(optional)" reads as though it could be dropped.
              <Field label="Email address" required>
                {/* Shown, not asked. The code has already proved this address,
                    and the server reads it from the session rather than from
                    anything typed here. */}
                {/* Sat between two bright white inputs as a 6%-white strip, so
                    it read as a text box that had failed to render rather than
                    as something already settled. A confirmed thing should look
                    unlike the things still being asked for: green tick first, a
                    real edge, and the address in white. */}
                <p
                  style={{
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "12px 14px",
                    minHeight: "var(--touch-target)",
                    boxSizing: "border-box",
                    borderRadius: "var(--radius)",
                    background: "rgba(142,224,107,.12)",
                    border: "2px solid rgba(142,224,107,.55)",
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 600,
                    // A long address must not push the tick off the chip.
                    overflowWrap: "anywhere",
                  }}
                >
                  <span aria-hidden="true" style={{ color: "var(--green)", fontWeight: 900 }}>
                    ✓
                  </span>
                  {verifiedEmail}
                  <span className="tq-visually-hidden"> (verified)</span>
                </p>
              </Field>
            ) : (
              <Field label="Email address" required>
                <input
                  className="tq-answer-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  maxLength={200}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                {/* Only reachable where sign-in is unavailable — a venue LAN
                    cannot send email. There the address is a claim, not proof,
                    and the seat is recorded as unverified. */}
                <p style={{ margin: "6px 2px 0", fontSize: 12, color: "var(--muted)" }}>
                  Used to save your history so you can pick up where you left off. We will not email
                  you during the game.
                </p>
              </Field>
            )}

            <Field label="Organization">
              <input
                className="tq-answer-input"
                type="text"
                autoComplete="organization"
                maxLength={120}
                value={organization}
                onChange={(event) => setOrganization(event.target.value)}
              />
            </Field>

            <Button
              variant="primary"
              size="lg"
              disabled={!detailsValid}
              onClick={() => setStep("consent")}
              style={{ width: "100%" }}
            >
              Continue
            </Button>
          </>
        )}

        {/* §36.4 — consent, before any photo is requested. Two appealing
            options, not an action and a penalty. */}
        {step === "consent" && (
          <>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: "var(--ink)" }}>
              Everyone gets a hand-drawn street-art avatar for the leaderboard. Which one do you
              want?
            </p>

            <ChoiceCard
              emoji="🎨"
              title="Make one from my photo"
              body="You take a selfie or upload a picture, and it is turned into a Florida street-art cartoon of you."
              // §36.4 — plain language about the third party, before the ask.
              note="Your photo is sent to an outside AI service to draw the avatar. It is never shown to other players, and you can delete it later."
              onSelect={() => setStep("photo")}
            />

            <ChoiceCard
              emoji="🦩"
              title="Give me an animal"
              body="You get a character in the exact same art style — a flamingo, a manatee, an alligator. No photo, no camera, nothing sent anywhere."
              onSelect={() => setStep("animal")}
            />
          </>
        )}

        {step === "photo" && (
          <PhotoStep
            photo={photo}
            onPhoto={setPhoto}
            onBack={() => setStep("consent")}
            onError={setError}
            busy={busy}
            onSubmit={() =>
              void submit({
                // §36.4 — an affirmative action; the timestamp is recorded.
                avatarConsent: true,
                photo,
                avatarAnimal: null,
              })
            }
          />
        )}

        {step === "animal" && (
          <AnimalStep
            busy={busy}
            onBack={() => setStep("consent")}
            onError={setError}
            onSubmit={(animal) =>
              void submit({
                // §36.4 — declining captures nothing. No photo is sent.
                avatarConsent: false,
                photo: null,
                avatarAnimal: animal,
              })
            }
          />
        )}
      </div>
    </div>
  );
}
